import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Minus, ShoppingBag, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { CATEGORIES, PRODUCTS, PACK_SIZES } from "@/data/products";
import { useCart } from "@/context/CartContext";

const displayTitle = (product) => {
    const name = product.name;
    // Strip trailing word if it's a known suffix to isolate the italic tail
    const suffixes = ["Powder", "Spice", "Flakes"];
    for (const s of suffixes) {
        if (name.endsWith(` ${s}`)) {
            return {
                head: name.slice(0, -s.length - 1),
                tail: ` ${s.toLowerCase()}.`,
            };
        }
    }
    // No known suffix — show name + category tail
    const tailByCategory = {
        Nut: " kernels.",
        Beverage: " blend.",
        Wellness: " root.",
    };
    return { head: name, tail: tailByCategory[product.category] || " · pure." };
};

const ProductCard = ({ product }) => {
    const [size, setSize] = useState("250g");
    const [qty, setQty] = useState(1);
    const { addItem, setOpen } = useCart();

    const handleAdd = (e) => {
        e.preventDefault();
        e.stopPropagation();
        addItem(product, size, qty);
        toast.success(`${product.name} · ${size} added`, {
            description: `Quantity: ${qty}`,
            action: {
                label: "View cart",
                onClick: () => setOpen(true),
            },
        });
    };

    return (
        <article
            data-testid={`product-card-${product.id}`}
            className="group relative bg-white rounded-3xl overflow-hidden border border-sun-line hover:shadow-xl hover:shadow-sun-orange/10 transition-all duration-500 flex flex-col"
        >
            <Link
                to={`/product/${product.id}`}
                data-testid={`product-link-${product.id}`}
                className="relative block aspect-square overflow-hidden bg-gradient-to-br from-white to-sun-cream"
            >
                <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-contain p-2 transition-transform duration-[1500ms] group-hover:scale-105"
                    loading="lazy"
                />
                <div className="absolute top-4 left-4 right-4 flex items-start justify-between pointer-events-none">
                    <span className="font-mono text-[10px] uppercase tracking-[0.22em] bg-white/95 backdrop-blur px-3 py-1.5 rounded-full text-sun-ink font-medium shadow-sm">
                        {product.category}
                    </span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.22em] bg-sun-ink text-sun-bg px-3 py-1.5 rounded-full font-medium shadow-sm">
                        100% pure
                    </span>
                </div>
            </Link>

            <div className="p-5 md:p-6 flex-1 flex flex-col">
                <Link to={`/product/${product.id}`} className="hover:text-sun-orange transition-colors">
                    <h3 className="font-heading text-3xl md:text-4xl leading-[0.95] tracking-tight text-sun-ink">
                        {displayTitle(product).head}
                        <span className="display-italic text-sun-orange text-2xl ml-1.5">
                            {displayTitle(product).tail}
                        </span>
                    </h3>
                </Link>
                <p className="mt-3 text-sm text-sun-inkSoft leading-relaxed line-clamp-2">
                    {product.tagline}
                </p>

                <div className="mt-5">
                    <div className="overline mb-2">— Pack size</div>
                    <div
                        role="radiogroup"
                        aria-label={`${product.name} pack size`}
                        className="grid grid-cols-3 gap-1 p-1 bg-sun-cream rounded-full"
                        data-testid={`pack-size-selector-${product.id}`}
                    >
                        {PACK_SIZES.map((s) => (
                            <button
                                key={s}
                                role="radio"
                                aria-checked={size === s}
                                onClick={() => setSize(s)}
                                data-testid={`pack-${product.id}-${s}`}
                                className={`py-2 font-mono text-[10px] uppercase tracking-[0.18em] rounded-full transition-colors ${
                                    size === s
                                        ? "bg-sun-ink text-sun-bg"
                                        : "text-sun-inkSoft hover:text-sun-ink"
                                }`}
                            >
                                {s}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="mt-4 flex items-end justify-between">
                    <div>
                        <div className="overline">— Price</div>
                        <div
                            data-testid={`product-price-${product.id}`}
                            className="font-heading text-3xl mt-1 text-sun-ink"
                        >
                            ₹{product.prices[size]}
                        </div>
                    </div>
                    <div className="flex items-center bg-sun-cream rounded-full">
                        <button
                            onClick={() => setQty((q) => Math.max(1, q - 1))}
                            data-testid={`qty-minus-${product.id}`}
                            className="w-9 h-9 flex items-center justify-center text-sun-inkSoft hover:text-sun-orange rounded-full"
                            aria-label="decrease quantity"
                        >
                            <Minus className="w-3.5 h-3.5" />
                        </button>
                        <div
                            className="w-7 text-center font-mono text-xs text-sun-ink font-semibold"
                            data-testid={`qty-display-${product.id}`}
                        >
                            {qty}
                        </div>
                        <button
                            onClick={() => setQty((q) => q + 1)}
                            data-testid={`qty-plus-${product.id}`}
                            className="w-9 h-9 flex items-center justify-center text-sun-inkSoft hover:text-sun-orange rounded-full"
                            aria-label="increase quantity"
                        >
                            <Plus className="w-3.5 h-3.5" />
                        </button>
                    </div>
                </div>

                <div className="mt-5 grid grid-cols-2 gap-2">
                    <button
                        onClick={handleAdd}
                        data-testid={`add-to-cart-${product.id}`}
                        className="inline-flex items-center justify-center gap-2 bg-sun-ink text-sun-bg hover:bg-sun-orange transition-colors duration-300 px-4 py-3 font-body text-xs uppercase tracking-[0.18em] font-medium rounded-full"
                    >
                        <ShoppingBag className="w-4 h-4" strokeWidth={2} />
                        Add
                    </button>
                    <Link
                        to={`/product/${product.id}`}
                        className="inline-flex items-center justify-center gap-2 border border-sun-ink/20 text-sun-ink hover:border-sun-orange hover:text-sun-orange transition-colors duration-300 px-4 py-3 font-body text-xs uppercase tracking-[0.18em] font-medium rounded-full"
                    >
                        Details
                        <ArrowUpRight className="w-3.5 h-3.5" />
                    </Link>
                </div>

                <div className="mt-5 pt-4 border-t border-sun-line flex flex-wrap gap-1.5 text-sun-muted">
                    {product.applications.map((a, i, arr) => (
                        <React.Fragment key={a}>
                            <span className="font-mono text-[10px] uppercase tracking-[0.18em]">
                                {a}
                            </span>
                            {i < arr.length - 1 && <span className="text-sun-orange">·</span>}
                        </React.Fragment>
                    ))}
                </div>
            </div>
        </article>
    );
};

const Products = () => {
    const [filter, setFilter] = useState("All");
    const filtered = useMemo(
        () => (filter === "All" ? PRODUCTS : PRODUCTS.filter((p) => p.category === filter)),
        [filter],
    );

    return (
        <section
            id="products"
            data-testid="products-section"
            className="py-24 md:py-36 bg-sun-bg relative"
        >
            <div className="container-x">
                <div className="grid grid-cols-12 gap-6 mb-12 md:mb-16 items-end">
                    <div className="col-span-12 md:col-span-8">
                        <div className="overline mb-6">— The catalogue</div>
                        <h2 className="font-heading text-5xl md:text-7xl lg:text-[7.5rem] leading-[0.95] tracking-[-0.02em] text-sun-ink">
                            {PRODUCTS.length}
                            <span className="display-italic text-gradient"> single-origin </span>
                            pantry staples.
                        </h2>
                    </div>
                    <div className="col-span-12 md:col-span-4 flex flex-col gap-6">
                        <div className="bg-sun-ink text-white rounded-2xl p-4 flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-sun-gold text-sun-ink flex items-center justify-center font-mono text-base font-bold flex-shrink-0">
                                %
                            </div>
                            <div className="leading-tight">
                                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-sun-goldSoft">
                                    Limited offer
                                </div>
                                <div className="text-sm mt-0.5">
                                    Get <span className="font-semibold text-sun-gold">10% off</span> with code{" "}
                                    <span className="font-mono font-semibold">AVAIL10</span> — auto applied at checkout
                                </div>
                            </div>
                        </div>
                        <p className="text-sun-inkSoft text-base leading-relaxed">
                            Each card below is one ingredient — milled fine and packed within
                            36 hours of harvest. Tap a pack size and add to cart.
                        </p>
                        <div className="flex flex-wrap gap-2">
                            {CATEGORIES.map((c) => (
                                <button
                                    key={c}
                                    onClick={() => setFilter(c)}
                                    data-testid={`filter-${c.toLowerCase()}`}
                                    className={`font-mono text-[10px] uppercase tracking-[0.22em] px-4 py-2 rounded-full transition-colors ${
                                        filter === c
                                            ? "bg-sun-ink text-sun-bg"
                                            : "bg-white border border-sun-line text-sun-inkSoft hover:border-sun-orange hover:text-sun-orange"
                                    }`}
                                >
                                    {c}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                <div
                    data-testid="products-grid"
                    className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5"
                >
                    {filtered.map((p) => (
                        <ProductCard key={p.id} product={p} />
                    ))}
                </div>

                <div className="mt-16 flex flex-col md:flex-row md:items-center md:justify-between gap-4 pt-8 border-t border-sun-line">
                    <p className="overline">— Need it by the tonne?</p>
                    <a href="#bulk" className="btn-gradient">
                        Open the trade desk
                        <ArrowUpRight className="w-4 h-4" />
                    </a>
                </div>
            </div>
        </section>
    );
};

export default Products;
